import { useCallback, useMemo, useState } from 'react'

import { Box, Button, type IllustrationType, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagStatus } from '@factorialco/f0-react'
import { ArrowRight } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { PayrollRunInfoPayrollRunFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import {
  PayrollRunStatus,
  getNextPayrollRunStatus,
  getPayrollRunStatusText,
  getPayrollRunStatusVariant,
} from 'modules/compensations/lib/payrollRunStatus'
import { useIntegrationsSyncContext } from 'modules/integrations/public'

type TransitionSource = 'export' | 'sync' | 'sendToBK'
type TransitionedStatuses = Exclude<PayrollRunStatus, typeof PayrollRunStatus.OPEN>
export type SourceStatuses = Exclude<PayrollRunStatus, typeof PayrollRunStatus.COMPLETED>

// TODO: We could potentially move this stuff to a centralized configuration
const STATUS_ILLUSTRATION = {
  [PayrollRunStatus.IN_PREPARATION]: 'permissions',
  [PayrollRunStatus.CALCULATION]: 'calculator',
  [PayrollRunStatus.IN_REVIEW]: 'task-list-search',
  [PayrollRunStatus.COMPLETED]: 'alert',
} satisfies Record<TransitionedStatuses, IllustrationType>

/**
 * Get the appropriate illustration for a status
 */
const getStatusIllustration = (status: TransitionedStatuses): IllustrationType => {
  return STATUS_ILLUSTRATION[status]
}

/**
 * Get the description for a specific transition source
 */
const getTransitionDescription = (source: TransitionSource): string => {
  if (source === 'export') {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.export'
    )
  } else if (source === 'sync') {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.sync'
    )
  } else if (source === 'sendToBK') {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.sendToBK'
    )
  }

  throw new Error(`Invalid transition source: ${source}`)
}

/**
 * Determine the appropriate transition source based on current and target status
 */
const determineTransitionSource = (
  isSyncEnabled: boolean,
  isBookkeeperEnabled?: boolean
): TransitionSource => {
  return isSyncEnabled ? 'sync' : isBookkeeperEnabled ? 'sendToBK' : 'export'
}

const getDescriptionText = (
  targetStatus: TransitionedStatuses,
  isSyncEnabled: boolean,
  isBookkeeperEnabled?: boolean
): string => {
  if (targetStatus === PayrollRunStatus.IN_REVIEW) {
    return i18n.t(
      'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.description.toInReview'
    )
  }

  const source = determineTransitionSource(isSyncEnabled, isBookkeeperEnabled)
  return getTransitionDescription(source)
}

/**
 * Checks if the payroll run is transitioning to a new status.
 * Returns false if the current status already matches the target status,
 * indicating no actual transition is occurring.
 *
 * @param currentStatus - The current status of the payroll run
 * @param targetStatus - The target status we're transitioning to
 * @returns true if this represents an actual status transition, false otherwise
 */
const isStatusTransition = (
  currentStatus: string | undefined,
  targetStatus: TransitionedStatuses
): boolean => {
  return currentStatus !== targetStatus
}

const getModalTitle = (
  period: string,
  targetStatus: TransitionedStatuses,
  isSyncEnabled: boolean,
  isBookkeeperEnabled?: boolean
): string => {
  if (targetStatus === PayrollRunStatus.CALCULATION) {
    const source = determineTransitionSource(isSyncEnabled, isBookkeeperEnabled)
    if (source === 'sendToBK') {
      return i18n.t(
        'compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.sendToBookkeeperTitle',
        { period }
      )
    }
  }

  return i18n.t('compensations.PayrollRunCompensationsList.payrollRunStatusTransitionModal.title', {
    period,
  })
}

const useCalculationToReviewModal = (payrollRunId: number | undefined, status?: string) => {
  const storageKey = `${LOCAL_STORAGE_KEYS.compensationsCalculationToReviewModalShown}_${payrollRunId}`
  const prevStatusStorageKey = `${LOCAL_STORAGE_KEYS.compensationsCalculationToReviewModalShown}_${payrollRunId}_prevStatus`

  const [dismissedInSession, setDismissedInSession] = useState(false)

  const modalDismissed = useMemo(() => {
    if (!payrollRunId) return false
    if (dismissedInSession) return true

    const raw = localStorage.getItem(storageKey)
    if (raw) {
      try {
        return JSON.parse(raw) === true
      } catch {
        return false
      }
    }
    return false
  }, [storageKey, payrollRunId, dismissedInSession])

  const setStoredPrevStatus = useCallback(
    (value: string) => {
      if (payrollRunId) {
        localStorage.setItem(prevStatusStorageKey, JSON.stringify(value))
      }
    },
    [prevStatusStorageKey, payrollRunId]
  )

  const handleClose = useCallback(() => {
    if (payrollRunId) {
      localStorage.setItem(storageKey, JSON.stringify(true))
      setDismissedInSession(true)
    }
  }, [storageKey, payrollRunId])

  const showModal = Boolean(payrollRunId && status === PayrollRunStatus.IN_REVIEW && !modalDismissed)

  return { showModal, handleClose, setStoredPrevStatus }
}

type PayrollRunStatusTransitionModalProps = {
  payrollRun: PayrollRunInfoPayrollRunFragment
  targetStatus: TransitionedStatuses
  isBookkeeperEnabled?: boolean
  prevStatus?: SourceStatuses
  onClose: () => void
}

const PayrollRunStatusTransitionModal = ({
  payrollRun,
  targetStatus,
  isBookkeeperEnabled,
  prevStatus: prevStatusProp,
  onClose,
}: PayrollRunStatusTransitionModalProps) => {
  const { syncEnabled, marketplaceIntegration } = useIntegrationsSyncContext()
  // TODO: we should abstract this somewhere else
  const isSyncEnabled = Boolean(syncEnabled && marketplaceIntegration)

  const period = stringToDate(payrollRun.startsOn, 'backend_datetime').format('MMMM YYYY')

  const title = getModalTitle(period, targetStatus, isSyncEnabled, isBookkeeperEnabled)

  // Handle race condition where payrollRun.status has already been updated to targetStatus
  // (e.g., via optimistic updates through updatePayrollRunStatus function) before the modal renders.
  // This ensures we show the correct transition (e.g., IN_PREPARATION → CALCULATION or CALCULATION → IN_REVIEW)
  // instead of showing the same status twice (e.g., CALCULATION → CALCULATION).
  // If prevStatus is explicitly provided, use it; otherwise, infer from payrollRun.status
  let prevStatus = prevStatusProp || payrollRun.status
  if (targetStatus === prevStatus && !prevStatusProp) {
    if (targetStatus === PayrollRunStatus.CALCULATION) {
      prevStatus = PayrollRunStatus.IN_PREPARATION
    } else if (targetStatus === PayrollRunStatus.IN_REVIEW) {
      // For IN_REVIEW, we use the most common previous status (CALCULATION)
      // This handles page refresh scenarios where we don't have the actual previous status
      // Note: This may show CALCULATION → IN_REVIEW even if the actual transition was OPEN → IN_REVIEW
      prevStatus = PayrollRunStatus.CALCULATION
    }
  }

  const nextStatus = targetStatus || getNextPayrollRunStatus(prevStatus)

  const currentStatusText = getPayrollRunStatusText(prevStatus)
  const nextStatusText = getPayrollRunStatusText(nextStatus)
  const currentStatusVariant = getPayrollRunStatusVariant(prevStatus)
  const nextStatusVariant = getPayrollRunStatusVariant(nextStatus)

  const descriptionText = getDescriptionText(targetStatus, isSyncEnabled, isBookkeeperEnabled)
  const illustration = getStatusIllustration(targetStatus)

  return (
    <Modal
      illustration={illustration}
      title={title}
      onClose={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {i18n.t('common.close')}
        </Button>
      }
    >
      <Box flexDirection="column" gap="s24">
        <Box alignItems="center" flexDirection="row" gap="s12">
          <F0TagStatus text={currentStatusText} variant={currentStatusVariant} />
          <F0Icon icon={ArrowRight} />
          <F0TagStatus text={nextStatusText} variant={nextStatusVariant} />
        </Box>
        <Box flexDirection="column" gap="s12">
          <Text.Normal size="300" color="textSecondary">
            {descriptionText}
          </Text.Normal>
        </Box>
      </Box>
    </Modal>
  )
}

export {
  PayrollRunStatusTransitionModal,
  getTransitionDescription,
  useCalculationToReviewModal,
  getDescriptionText,
  isStatusTransition,
  getModalTitle,
}
export type { PayrollRunStatusTransitionModalProps }
